import { createSelector } from "@reduxjs/toolkit";
import { mapKeys, merge, reduce } from "lodash-es";

import { API_ENDPOINTS } from "shared/constants";
import { IDataKeys, IJSONAPIItem } from "shared/types";
import { buildSingleItem } from "shared/utils";

import { IBandSubscription, ICompany, IInvoice } from "Bands/types";
import { buildData, dataSelector } from "./helpers";

const companiesSelector = createSelector(
  dataSelector,
  (data) => buildData(data as any, IDataKeys.company) as ICompany[]
);

const userCompanyIdSelector = createSelector(
  dataSelector,
  (data) => buildData(data as any, IDataKeys.userCompanies) as IJSONAPIItem[]
);

const mapIdsToCompanies = (
  companyIds: IJSONAPIItem[],
  companies: ICompany[]
) => {
  const companyMap = mapKeys(companies, ({ id }) => id);
  return reduce(
    companyIds,
    (userCompanies: ICompany[], { id, ...companyProps }) => {
      const company = merge({}, { id }, companyProps, companyMap?.[id]);

      if (company) {
        return [...userCompanies, company];
      }
      return userCompanies;
    },
    []
  );
};

export const userCompaniesSelector = createSelector(
  [userCompanyIdSelector, companiesSelector],
  (useCompanyIds, companies) => mapIdsToCompanies(useCompanyIds, companies)
);

const dataCompanySelector = createSelector(
  dataSelector,
  (data) => data.company
);
const dataUserCompaniesSelector = createSelector(
  dataSelector,
  (data) => data.userCompanies
);
export const companySelectorCreator = (id: string) =>
  createSelector(
    dataCompanySelector,
    dataUserCompaniesSelector,
    (companies, userCompanies) => {
      const userCompany = userCompanies?.[API_ENDPOINTS.COMPANY(id)];
      const company = companies?.[API_ENDPOINTS.COMPANY(id)];

      if (company || userCompany) {
        return buildSingleItem(merge({}, userCompany, company)) as ICompany;
      }
      return null;
    }
  );

export const companyInvoiceSelector = createSelector(
  [dataSelector],
  (data) =>
    (buildData(data as any, IDataKeys.companyInvoices) as IInvoice[]) || []
);

export const paymentInfoSelector = createSelector(
  [dataSelector],
  (data) => data[IDataKeys.paymentInfo]
);

export const companySubscriptionsSelector = createSelector(
  [dataSelector],
  (data) =>
    (buildData(
      data as any,
      IDataKeys.companySubscriptions
    ) as IBandSubscription[]) || []
);

const dataInvoiceSelector = createSelector(
  dataSelector,
  (data) => data.invoice
);

export const invoiceSelectorCreator = (id: string) =>
  createSelector(dataInvoiceSelector, (invoices) => {
    const invoice = invoices?.[API_ENDPOINTS.INVOICE(id)];
    if (invoice) {
      return buildSingleItem(invoice) as IInvoice;
    }
    return null;
  });
